import React, { useMemo, useState, useRef, useEffect, useLayoutEffect } from "react";
import {
  FiAlertTriangle,
  FiCheckCircle,
  FiSettings,
  FiUser,
  FiExternalLink,
  FiRefreshCw,
  FiCalendar,
  FiX,
  FiArrowUpCircle,
} from "react-icons/fi";
import { FaArrowUp, FaArrowDown } from "react-icons/fa";
import { MdPersonAddAlt1 } from "react-icons/md";
import { STATUS_OPTIONS } from "./studentStyles";
import { formatShortDate, getStudentFirstSessionDate, parseStudentDate } from "./studentUtils";
import { normalizeStudentStatus } from "../../../utils/studentLifecycle";
import "./StudentDirectory.css";

const INACTIVE_STATUSES = ["Finished", "No Answer", "Not Interested"];
const MAX_SCHOOL_GRADE = 12; // grades above this are treated as "graduating" on advancement

function normalizeStatusValue(status) {
  return normalizeStudentStatus(status);
}

function normalizeSortKey(value) {
  const raw = String(value || "").trim();
  if (raw === "nameAsc") return "nameAsc";
  if (raw === "nameDesc") return "nameDesc";
  if (raw === "gradeAsc") return "gradeAsc";
  if (raw === "gradeDesc") return "gradeDesc";
  if (raw === "firstAttendNewest") return "firstAttendNewest";
  if (raw === "firstAttendOldest") return "firstAttendOldest";
  if (raw === "hoursDesc") return "hoursDesc";
  if (raw === "hoursAsc") return "hoursAsc";

  // Fallbacks
  if (raw === "name") return "nameAsc";
  if (raw === "grade") return "gradeAsc";
  if (raw === "firstSession") return "firstAttendNewest";

  return "firstAttendNewest";
}

function startOfDayMs(value = new Date()) {
  const d = value instanceof Date ? new Date(value) : new Date(value);
  if (Number.isNaN(d.getTime())) return 0;
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

function formatLastSessionRelative(value, now = new Date()) {
  const sessionDate = parseStudentDate(value);
  if (!sessionDate) return "";
  const diffDays = Math.floor((startOfDayMs(now) - startOfDayMs(sessionDate)) / 86400000);
  if (diffDays <= 0) return "Today";
  if (diffDays === 1) return "1 day ago";
  return `${diffDays} days ago`;
}

function formatNextSessionRelative(value, now = new Date()) {
  const sessionDate = parseStudentDate(value);
  if (!sessionDate) return "";
  const diffDays = Math.floor((startOfDayMs(sessionDate) - startOfDayMs(now)) / 86400000);
  if (diffDays <= 0) return "Today";
  if (diffDays === 1) return "Tomorrow";
  return `in ${diffDays} days`;
}

function getLatestConferenceDate(student) {
  const conferences = Array.isArray(student?.conferences) ? student.conferences : [];
  let latest = null;
  for (let i = 0; i < conferences.length; i += 1) {
    const entry = conferences[i];
    const parsed = parseStudentDate(entry?.date) || parseStudentDate(entry?.createdAtMs);
    if (!parsed) continue;
    if (!latest || parsed.getTime() > latest.getTime()) latest = parsed;
  }
  return latest;
}

function buildConferenceMeta(student) {
  const totalHoursRaw = Number(student?.totalSessionHours);
  const totalHours = Number.isFinite(totalHoursRaw) ? Math.max(0, totalHoursRaw) : 0;
  const latestConferenceDate = getLatestConferenceDate(student);

  const conferenceBaselineDate = parseStudentDate(student?.conferenceLastCompletedAt) || latestConferenceDate;
  const conferenceBaselineHoursRaw = Number(student?.conferenceLastCompletedHours);
  const conferenceBaselineHours = Number.isFinite(conferenceBaselineHoursRaw) && conferenceBaselineHoursRaw >= 0
    ? conferenceBaselineHoursRaw
    : 0;

  const cycleResetDate = parseStudentDate(student?.conferenceCycleResetAt);
  const resetHoursRaw = Number(student?.conferenceCycleResetHours);
  const hasResetHours = Number.isFinite(resetHoursRaw) && resetHoursRaw >= 0;

  let baselineDate = conferenceBaselineDate;
  let baselineSource = conferenceBaselineDate ? "conference" : "none";
  let baselineHours = conferenceBaselineHours;

  if (cycleResetDate && (!baselineDate || cycleResetDate.getTime() > baselineDate.getTime())) {
    baselineDate = cycleResetDate;
    baselineSource = "reset";
    baselineHours = hasResetHours ? resetHoursRaw : totalHours;
  }

  const hoursSinceBaseline = Math.max(0, totalHours - baselineHours);
  const conferenceNeeded = hoursSinceBaseline >= 25;

  return {
    latestConferenceDate,
    baselineDate,
    baselineSource,
    baselineHours,
    totalHours,
    hoursSinceBaseline,
    conferenceNeeded,
  };
}

// Sort indicator arrow for the active sort column.
function SortIcon({ active, ascending }) {
  if (!active) return null;
  return ascending
    ? <FaArrowUp className="sd-sort-icon" />
    : <FaArrowDown className="sd-sort-icon" />;
}

// ────────────────────────────────────────────────────────────────
// Row right-click context menu (fixed to the cursor, viewport-clamped)
// ────────────────────────────────────────────────────────────────
function RowContextMenu({ items, x, y, onClose }) {
  const menuRef = useRef(null);
  const [pos, setPos] = useState({ x, y });

  useLayoutEffect(() => {
    setPos({ x, y });
  }, [x, y]);

  // Clamp to viewport once measured.
  useLayoutEffect(() => {
    const node = menuRef.current;
    if (!node) return;
    const rect = node.getBoundingClientRect();
    const pad = 8;
    let nextX = x;
    let nextY = y;
    if (nextX + rect.width > window.innerWidth - pad) nextX = Math.max(pad, window.innerWidth - rect.width - pad);
    if (nextY + rect.height > window.innerHeight - pad) nextY = Math.max(pad, window.innerHeight - rect.height - pad);
    setPos({ x: nextX, y: nextY });
  }, [x, y]);

  useEffect(() => {
    function onKey(event) {
      if (event.key === "Escape") onClose();
    }
    function onScroll() {
      onClose();
    }
    document.addEventListener("keydown", onKey);
    window.addEventListener("resize", onClose);
    // Capture phase so scrolling within the list container also closes it.
    window.addEventListener("scroll", onScroll, true);
    return () => {
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", onClose);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [onClose]);

  return (
    <>
      <div className="sd-ctx-backdrop" onClick={onClose} onContextMenu={(e) => { e.preventDefault(); onClose(); }} />
      <div
        ref={menuRef}
        className="sd-ctx-menu"
        role="menu"
        style={{ top: pos.y, left: pos.x }}
        onClick={(e) => e.stopPropagation()}
      >
        {items.map((item, index) => {
          if (item.type === "sep") {
            return <div key={`sep-${index}`} className="sd-ctx-sep" />;
          }
          return (
            <button
              key={item.key}
              type="button"
              role="menuitem"
              className={`sd-ctx-item ${item.danger ? "sd-ctx-item--danger" : ""}`}
              disabled={item.disabled}
              onClick={() => {
                if (item.disabled) return;
                onClose();
                item.onClick?.();
              }}
            >
              {item.icon && <span className="sd-ctx-icon">{item.icon}</span>}
              <span className="sd-ctx-label">{item.label}</span>
            </button>
          );
        })}
      </div>
    </>
  );
}

// ────────────────────────────────────────────────────────────────
// Settings: bulk Grade Advancement tool
// ────────────────────────────────────────────────────────────────
function GradeAdvancementTool({ students, onApply }) {
  // Students that carry a usable numeric grade are the only ones we can advance.
  const advanceable = useMemo(() => {
    return (students || [])
      .filter((s) => {
        const raw = s?.grade;
        return raw != null && String(raw).trim() !== "" && Number.isFinite(Number(raw));
      })
      .map((s) => {
        const current = Number(s.grade);
        const status = normalizeStatusValue(s?.status);
        const isInactive = INACTIVE_STATUSES.includes(status);
        const isGraduating = current >= MAX_SCHOOL_GRADE;
        return { id: s.id, name: s?.name || "—", status, current, next: current + 1, isInactive, isGraduating };
      })
      .sort((a, b) => String(a.name).localeCompare(String(b.name)));
  }, [students]);

  const skippedCount = (students || []).length - advanceable.length;

  // Default selection: active students who are not yet graduating.
  const [selectedIds, setSelectedIds] = useState(
    () => new Set(advanceable.filter((s) => !s.isInactive && !s.isGraduating).map((s) => s.id))
  );
  const [saving, setSaving] = useState(false);
  const [doneCount, setDoneCount] = useState(0);

  const selectedRows = advanceable.filter((s) => selectedIds.has(s.id));
  const allSelected = advanceable.length > 0 && selectedRows.length === advanceable.length;

  const toggleOne = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    setSelectedIds(allSelected ? new Set() : new Set(advanceable.map((s) => s.id)));
  };

  async function handleApply() {
    if (!selectedRows.length || saving) return;
    const graduatingCount = selectedRows.filter((s) => s.isGraduating).length;
    const warning = graduatingCount
      ? `\n\n${graduatingCount} selected student(s) are at grade ${MAX_SCHOOL_GRADE} and will be advanced beyond it.`
      : "";
    const ok = window.confirm(
      `Advance ${selectedRows.length} student(s) by one grade level? This updates the grade on each student's profile.${warning}`
    );
    if (!ok) return;

    const updates = selectedRows.map((s) => ({ id: s.id, grade: s.next }));
    setSaving(true);
    try {
      await onApply(updates);
      setDoneCount(updates.length);
    } catch (e) {
      alert(`Grade advancement failed: ${e.message}`);
    } finally {
      setSaving(false);
    }
  }

  if (doneCount > 0) {
    return (
      <div className="sd-tool-done">
        <FiCheckCircle size={34} className="sd-tool-done-icon" />
        <div className="sd-tool-done-title">Grades advanced</div>
        <div className="sd-tool-done-text">
          {doneCount} student{doneCount === 1 ? "" : "s"} moved up one grade level.
        </div>
      </div>
    );
  }

  return (
    <div className="sd-tool">
      <div className="sd-tool-head">
        <div className="sd-tool-title">
          <FiArrowUpCircle size={18} />
          Grade Advancement
        </div>
        <p className="sd-tool-desc">
          Move students up one grade level for the new school year. Review the list and uncheck anyone who
          should be left unchanged before applying.
        </p>
      </div>

      {advanceable.length === 0 ? (
        <div className="sd-tool-empty">No students with a numeric grade are available to advance.</div>
      ) : (
        <>
          <div className="sd-adv-toolbar">
            <label className="sd-adv-selectall">
              <input type="checkbox" checked={allSelected} onChange={toggleAll} />
              {allSelected ? "Deselect all" : "Select all"}
            </label>
            <span className="sd-adv-count">{selectedRows.length} of {advanceable.length} selected</span>
          </div>

          <div className="sd-adv-list">
            <div className="sd-adv-grid sd-adv-head">
              <div />
              <div>Name</div>
              <div>Status</div>
              <div className="sd-adv-grade-col">Grade</div>
            </div>
            {advanceable.map((s) => {
              const checked = selectedIds.has(s.id);
              return (
                <label key={s.id} className={`sd-adv-grid sd-adv-row ${checked ? "" : "sd-adv-row--off"}`}>
                  <input type="checkbox" checked={checked} onChange={() => toggleOne(s.id)} />
                  <div className="sd-adv-name">{s.name}</div>
                  <div>
                    <span className={`sd-status-badge sd-status--${s.status.toLowerCase().replace(/\s+/g, "-")}`}>
                      {s.status}
                    </span>
                  </div>
                  <div className="sd-adv-grade-col">
                    <span className="sd-adv-grade-from">{s.current}</span>
                    <span className="sd-adv-arrow">→</span>
                    <span className="sd-adv-grade-to">{s.next}</span>
                    {s.isGraduating && (
                      <span className="sd-adv-flag" title={`Beyond grade ${MAX_SCHOOL_GRADE}`}>
                        <FiAlertTriangle size={12} />
                      </span>
                    )}
                  </div>
                </label>
              );
            })}
          </div>

          {skippedCount > 0 && (
            <div className="sd-tool-note">
              {skippedCount} student{skippedCount === 1 ? "" : "s"} without a numeric grade {skippedCount === 1 ? "was" : "were"} skipped.
            </div>
          )}

          <div className="sd-tool-footer">
            <button
              type="button"
              className="sd-btn sd-btn--primary"
              disabled={!selectedRows.length || saving}
              onClick={handleApply}
            >
              {saving ? "Applying…" : `Advance ${selectedRows.length} student${selectedRows.length === 1 ? "" : "s"}`}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// Settings modal — sectioned shell so future directory tools slot in
// ────────────────────────────────────────────────────────────────
function DirectorySettingsModal({ students, onClose, onBulkUpdateGrades }) {
  const tools = useMemo(
    () => [
      {
        key: "grade-advancement",
        label: "Grade Advancement",
        icon: <FiArrowUpCircle size={16} />,
        render: () => <GradeAdvancementTool students={students} onApply={onBulkUpdateGrades} />,
      },
      // Future directory-wide tools register here.
    ],
    [students, onBulkUpdateGrades]
  );

  const [activeKey, setActiveKey] = useState(tools[0]?.key || "");
  const activeTool = tools.find((t) => t.key === activeKey) || tools[0];

  useEffect(() => {
    function onKey(event) {
      if (event.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="sd-settings-overlay" onMouseDown={onClose}>
      <div className="sd-settings-modal" onMouseDown={(e) => e.stopPropagation()}>
        <div className="sd-settings-header">
          <div className="sd-settings-title">
            <FiSettings size={18} />
            Directory Settings
          </div>
          <button type="button" className="sd-icon-btn" onClick={onClose} aria-label="Close settings">
            <FiX size={18} />
          </button>
        </div>
        <div className="sd-settings-body">
          <nav className="sd-settings-nav">
            {tools.map((tool) => (
              <button
                key={tool.key}
                type="button"
                className={`sd-settings-nav-item ${tool.key === activeTool?.key ? "sd-settings-nav-item--active" : ""}`}
                onClick={() => setActiveKey(tool.key)}
              >
                {tool.icon}
                {tool.label}
              </button>
            ))}
          </nav>
          <div className="sd-settings-content">{activeTool?.render()}</div>
        </div>
      </div>
    </div>
  );
}

export default function StudentDirectory({
  students,
  loading,
  onCreateNew,
  onOpenStudent,
  onUpdateStatus,
  onSkipConferenceCycle,
  onBulkUpdateGrades,
  getStudentHref,
  searchTerm: searchTermProp,
  filterStatus: filterStatusProp,
  sortKey: sortKeyProp,
  onDirectoryStateChange,
  allowCreate = true,
  allowStatusEdit = true,
  allowConferenceSkip = true,
}) {
  const [searchTermLocal, setSearchTermLocal] = useState("");
  const [filterStatusLocal, setFilterStatusLocal] = useState("All");
  const [sortKeyLocal, setSortKeyLocal] = useState("firstAttendNewest");
  const [conferenceFilter, setConferenceFilter] = useState("all");
  const [savingConferenceRowId, setSavingConferenceRowId] = useState("");
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
  const [contextMenu, setContextMenu] = useState(null); // { x, y, items }
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const statusDropdownRef = useRef(null);

  // Admins (those who can skip conference cycles) get row management actions + settings.
  const canManage = allowConferenceSkip && !!onSkipConferenceCycle;
  const canOpenSettings = canManage && typeof onBulkUpdateGrades === "function";

  useEffect(() => {
    function handleClickOutside(event) {
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target)) {
        setIsStatusDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const searchTerm = searchTermProp ?? searchTermLocal;
  const filterStatus = filterStatusProp ?? filterStatusLocal;
  const sortKey = normalizeSortKey(sortKeyProp ?? sortKeyLocal);

  const setSearchTerm = (value) => {
    if (onDirectoryStateChange) onDirectoryStateChange({ searchTerm: value });
    else setSearchTermLocal(value);
  };

  const setFilterStatus = (value) => {
    if (onDirectoryStateChange) onDirectoryStateChange({ filterStatus: value });
    else setFilterStatusLocal(value);
  };

  const setSortKey = (value) => {
    if (onDirectoryStateChange) onDirectoryStateChange({ sortKey: value });
    else setSortKeyLocal(value);
  };

  const rows = useMemo(() => {
    return (students || []).map((student) => {
      const firstAttendDate = getStudentFirstSessionDate(student);
      const lastSessionDate = parseStudentDate(student?.endDate || student?.lastSessionDate || "");
      const conferenceMeta = buildConferenceMeta(student);

      return {
        student,
        firstAttendDate,
        lastSessionDate,
        conferenceMeta,
      };
    });
  }, [students]);

  const filteredAndSorted = useMemo(() => {
    const filtered = rows.filter(({ student, conferenceMeta }) => {
      const name = String(student?.name || "").toLowerCase();
      if (searchTerm && !name.includes(searchTerm.toLowerCase().trim())) return false;

      const studentStatus = normalizeStatusValue(student?.status).toLowerCase();
      const activeFilters = Array.isArray(filterStatus) ? filterStatus : (filterStatus === "All" || !filterStatus ? [] : [filterStatus]);

      if (activeFilters.length > 0) {
        const match = activeFilters.some(f => f.toLowerCase() === studentStatus);
        if (!match) return false;
      }

      const isInactiveStatus = ["finished", "no answer", "not interested"].includes(studentStatus);
          if (conferenceFilter === "needed") {
            if (!conferenceMeta.conferenceNeeded || isInactiveStatus) return false;
          }
          if (conferenceFilter === "ok") {
            if (conferenceMeta.conferenceNeeded || isInactiveStatus) return false;
          }
      return true;
    });

    return [...filtered].sort((a, b) => {
      if (sortKey === "nameAsc" || sortKey === "nameDesc") {
        const diff = String(a.student?.name || "").localeCompare(String(b.student?.name || ""));
        return sortKey === "nameAsc" ? diff : -diff;
      }

      if (sortKey === "gradeAsc" || sortKey === "gradeDesc") {
        const diff = (Number(a.student?.grade) || 0) - (Number(b.student?.grade) || 0);
        return sortKey === "gradeAsc" ? diff : -diff;
      }

      if (sortKey === "hoursDesc" || sortKey === "hoursAsc") {
        const diff = (Number(b.conferenceMeta?.totalHours) || 0) - (Number(a.conferenceMeta?.totalHours) || 0);
        return sortKey === "hoursDesc" ? diff : -diff;
      }

      const aMs = a.firstAttendDate?.getTime?.() || 0;
      const bMs = b.firstAttendDate?.getTime?.() || 0;

      if (sortKey === "firstAttendNewest") {
        if (aMs && bMs && aMs !== bMs) return bMs - aMs;
        if (aMs && !bMs) return -1;
        if (!aMs && bMs) return 1;
      } else if (sortKey === "firstAttendOldest") {
        if (aMs && bMs && aMs !== bMs) return aMs - bMs;
        if (aMs && !bMs) return -1;
        if (!aMs && bMs) return 1;
      }

      return String(a.student?.name || "").localeCompare(String(b.student?.name || ""));
    });
  }, [rows, searchTerm, filterStatus, sortKey, conferenceFilter]);

  async function handleSkipConference(studentId, totalHours) {
    if (!canManage) return;
    setSavingConferenceRowId(studentId);
    try {
      await onSkipConferenceCycle(studentId, totalHours);
    } finally {
      setSavingConferenceRowId("");
    }
  }

  // Build the right-click action list for a row and open the context menu at the cursor.
  function openRowContextMenu(event, { student, conferenceMeta, canSkip }) {
    event.preventDefault();
    event.stopPropagation();

    const items = [
      {
        key: "open",
        label: "Open Student Profile",
        icon: <FiUser size={15} />,
        onClick: () => onOpenStudent(student),
      },
    ];

    if (typeof getStudentHref === "function") {
      items.push({
        key: "open-new-tab",
        label: "Open in New Tab",
        icon: <FiExternalLink size={15} />,
        onClick: () => {
          const href = getStudentHref(student);
          if (href) window.open(href, "_blank", "noopener");
        },
      });
    }

    if (canManage) {
      items.push({ type: "sep" });
      items.push({
        key: "conference",
        label: "Conference Management",
        icon: <FiCalendar size={15} />,
        onClick: () => onOpenStudent(student, { tab: "conference" }),
      });
      items.push({
        key: "skip-conference",
        label: "Skip & Restart 25 Hours",
        icon: <FiRefreshCw size={15} />,
        disabled: !canSkip || savingConferenceRowId === student.id,
        onClick: () => handleSkipConference(student.id, conferenceMeta.totalHours),
      });
    }

    setContextMenu({ x: event.clientX, y: event.clientY, items });
  }

  const activeFilters = Array.isArray(filterStatus) ? filterStatus : (filterStatus === "All" || !filterStatus ? [] : [filterStatus]);

  const toggleStatus = (opt) => {
    let next;
    if (opt === "All") {
      next = []; // Empty array means "All"
    } else {
      if (activeFilters.includes(opt)) {
        next = activeFilters.filter((s) => s !== opt);
      } else {
        next = [...activeFilters, opt];
      }
    }
    setFilterStatus(next.length === 0 ? "All" : next);
  };

  const toggleSort = (ascKey, descKey) => {
    setSortKey(sortKey === ascKey ? descKey : ascKey);
  };

  const now = new Date();
  const todayStart = startOfDayMs(now);

  return (
    <div className="student-directory">
      {/* Single compact toolbar: search · filters · settings · create */}
      <div className="sd-controls">
        <div className="sd-search-wrap">
          <input
            className="sd-search"
            placeholder="Search by name…"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
          />
          {searchTerm && (
            <button type="button" className="sd-search-clear" onClick={() => setSearchTerm("")} title="Clear">
              ×
            </button>
          )}
        </div>

        <div className="sd-multi" ref={statusDropdownRef}>
          <button
            type="button"
            className="sd-select sd-multi-btn"
            onClick={() => setIsStatusDropdownOpen(!isStatusDropdownOpen)}
          >
            {activeFilters.length === 0 ? "Status: All" : `Status: ${activeFilters.length} selected`}
          </button>

          {isStatusDropdownOpen && (
            <div className="sd-multi-menu">
              <div className="sd-multi-item" onClick={() => toggleStatus("All")}>
                <span className="sd-multi-check">{activeFilters.length === 0 ? "✓" : ""}</span>
                All
              </div>
              {STATUS_OPTIONS.map((opt) => (
                <div key={opt} className="sd-multi-item" onClick={() => toggleStatus(opt)}>
                  <span className="sd-multi-check">{activeFilters.includes(opt) ? "✓" : ""}</span>
                  {opt}
                </div>
              ))}
            </div>
          )}
        </div>

        <select className="sd-select" value={conferenceFilter} onChange={(event) => setConferenceFilter(event.target.value)}>
          <option value="all">Conference: All</option>
          <option value="needed">Conference: Needed</option>
          <option value="ok">Conference: Not Needed</option>
        </select>

        <div className="sd-toolbar-actions">
          {canOpenSettings && (
            <button
              type="button"
              className="sd-icon-btn sd-settings-btn"
              onClick={() => setIsSettingsOpen(true)}
              title="Directory settings"
              aria-label="Directory settings"
            >
              <FiSettings size={17} />
            </button>
          )}
          {allowCreate && (
            <button type="button" className="sd-create-btn" onClick={onCreateNew} title="Create student">
              <MdPersonAddAlt1 size={17} />
              <span className="sd-create-label">Create</span>
            </button>
          )}
        </div>
      </div>

      {/* List */}
      <div className="sd-card">
        <div className="sd-scroll">
          {/* Column header */}
          <div className="sd-grid sd-head">
            <div
              className="sd-head-cell sd-head-cell--sortable"
              onClick={() => toggleSort("nameAsc", "nameDesc")}
            >
              Name
              <SortIcon active={sortKey === "nameAsc" || sortKey === "nameDesc"} ascending={sortKey === "nameAsc"} />
            </div>
            <div
              className="sd-head-cell sd-head-cell--sortable"
              onClick={() => toggleSort("gradeAsc", "gradeDesc")}
            >
              Grade
              <SortIcon active={sortKey === "gradeAsc" || sortKey === "gradeDesc"} ascending={sortKey === "gradeAsc"} />
            </div>
            <div className="sd-head-cell">Status</div>
            <div
              className="sd-head-cell sd-head-cell--sortable"
              onClick={() => toggleSort("firstAttendOldest", "firstAttendNewest")}
            >
              Sign Up
              <SortIcon
                active={sortKey === "firstAttendNewest" || sortKey === "firstAttendOldest"}
                ascending={sortKey === "firstAttendOldest"}
              />
            </div>
            <div className="sd-head-cell">Last Session</div>
            <div className="sd-head-cell">Next Session</div>
            <div
              className="sd-head-cell sd-head-cell--sortable"
              onClick={() => toggleSort("hoursDesc", "hoursAsc")}
            >
              Hours
              <SortIcon active={sortKey === "hoursDesc" || sortKey === "hoursAsc"} ascending={sortKey === "hoursAsc"} />
            </div>
            <div className="sd-head-cell">Conference</div>
          </div>

          {/* Rows */}
          {loading ? (
            <div className="sd-empty">Loading students…</div>
          ) : filteredAndSorted.length === 0 ? (
            <div className="sd-empty">No students found.</div>
          ) : (
            filteredAndSorted.map(({ student, conferenceMeta, lastSessionDate }) => {
              const status = normalizeStatusValue(student?.status);
              const statusClassKey = status.toLowerCase().replace(/\s+/g, "-");
              const hasSession = !!lastSessionDate;
              const nextSessionDate = parseStudentDate(student?.nextSessionDate || "");
              const hasUpcomingSession = !!nextSessionDate && startOfDayMs(nextSessionDate) >= todayStart;
              const signUpDate = formatShortDate(
                student?.startDate || student?.firstSessionDate || student?.firstSession || student?.createdAt
              );
              const isInactive = INACTIVE_STATUSES.includes(status);
              const showConference = !isInactive && hasSession;
              const canSkip = showConference && conferenceMeta.conferenceNeeded && canManage;

              return (
                <div
                  className="sd-grid sd-row"
                  key={student.id}
                  onClick={() => onOpenStudent(student)}
                  onContextMenu={(event) => openRowContextMenu(event, { student, conferenceMeta, canSkip })}
                >
                  {/* Name */}
                  <div className="sd-name">{student?.name || "—"}</div>

                  {/* Grade */}
                  <div>
                    {student?.grade != null && String(student.grade) !== ""
                      ? <span className="sd-grade">{student.grade}</span>
                      : <span className="sd-muted">—</span>}
                  </div>

                  {/* Status */}
                  <div onClick={(event) => event.stopPropagation()}>
                    {allowStatusEdit ? (
                      <select
                        className={`sd-status-select sd-status--${statusClassKey}`}
                        value={status}
                        onChange={(event) => onUpdateStatus(student.id, event.target.value)}
                      >
                        {STATUS_OPTIONS.map((opt) => (
                          <option key={opt} value={opt}>
                            {opt}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span className={`sd-status-badge sd-status--${statusClassKey}`}>{status}</span>
                    )}
                  </div>

                  {/* Sign Up */}
                  <div>
                    {signUpDate && signUpDate !== "-"
                      ? <span className="sd-date">{signUpDate}</span>
                      : <span className="sd-muted">—</span>}
                  </div>

                  {/* Last Session */}
                  <div>
                    {lastSessionDate ? (
                      <div>
                        <div className="sd-date">{formatShortDate(lastSessionDate)}</div>
                        <div className="sd-date-rel">{formatLastSessionRelative(lastSessionDate, now)}</div>
                      </div>
                    ) : (
                      <span className="sd-muted">—</span>
                    )}
                  </div>

                  {/* Next Session */}
                  <div>
                    {hasUpcomingSession ? (
                      <div>
                        <div className="sd-date">{formatShortDate(nextSessionDate)}</div>
                        <div className="sd-date-rel">{formatNextSessionRelative(nextSessionDate, now)}</div>
                      </div>
                    ) : (
                      <span className="sd-muted">—</span>
                    )}
                  </div>

                  {/* Total Hours */}
                  <div>
                    {hasSession
                      ? <span className="sd-hours">{parseFloat(Number(conferenceMeta.totalHours || 0).toFixed(1))}h</span>
                      : <span className="sd-muted">—</span>}
                  </div>

                  {/* Conference — concise status only */}
                  <div>
                    {showConference ? (
                      <div className="sd-conf">
                        <div
                          className={`sd-conf-badge ${
                            conferenceMeta.conferenceNeeded ? "sd-conf-badge--due" : "sd-conf-badge--ok"
                          }`}
                        >
                          {conferenceMeta.conferenceNeeded ? <FiAlertTriangle size={12} /> : <FiCheckCircle size={12} />}
                          <span>
                            {conferenceMeta.conferenceNeeded ? "Conference Due" : "On Track"}
                            {" · "}
                            {Number(conferenceMeta.hoursSinceBaseline || 0).toFixed(1)}/25h
                          </span>
                        </div>
                        {conferenceMeta.latestConferenceDate && (
                          <div className="sd-conf-date">
                            Last {formatShortDate(conferenceMeta.latestConferenceDate)}
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="sd-muted">—</span>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {contextMenu && (
        <RowContextMenu
          items={contextMenu.items}
          x={contextMenu.x}
          y={contextMenu.y}
          onClose={() => setContextMenu(null)}
        />
      )}

      {isSettingsOpen && canOpenSettings && (
        <DirectorySettingsModal
          students={students}
          onBulkUpdateGrades={onBulkUpdateGrades}
          onClose={() => setIsSettingsOpen(false)}
        />
      )}
    </div>
  );
}
